import {
  Center,
  Flex,
  Spinner,
  Text,
  Box,
  ListItem,
  UnorderedList,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { StudentScheduleItem } from '@duke-hq/svc-api/student/services/schedulerService';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { AdminCourseScheduleItemType } from '@duke-hq/shared-constants/course';

export function CourseReview({ course }: { course: { id: string } }) {
  const { id } = course;
  const { data, isLoading } = trpcTenant.courses.byId.useQuery(id);

  if (isLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (data == null) {
    return <div>No course found</div>;
  }

  const hasWorkPlacement = data.courseItems.some(
    (item) => item.unitOfCompetency?.work_placement === true,
  );
  const schedulerData: AdminCourseScheduleItemType[] = data.schedule.filter(
    (item: { type: string }) => item.type === 'UNIT',
  );

  return (
    <VStack w="full" h="full" justify="start" align="start" gap="10px">
      <VStack w="full" justify="start" align="start" gap="6px">
        {data.identifier != null ? (
          <Text fontSize="md" lineHeight="6" color="fg.muted">
            {data.identifier}
          </Text>
        ) : null}
        <Box overflow="hidden" alignSelf="stretch">
          <Text fontSize="2xl" lineHeight="8" color="fg.default" fontWeight="bold">
            {data.kind === CourseKindSchema.enum.SKILL_SET
              ? `Skill set: ${data.title}`
              : data.title}
          </Text>
        </Box>
        <HStack gap="8px" justify="start" align="center">
          <HStack gap="4px" justify="start" align="center">
            <Text fontSize="sm" lineHeight="5" color="fg.muted">
              <Trans>Cost</Trans>:
            </Text>
            <Text fontSize="sm" lineHeight="5" color="fg.default" fontWeight="medium">
              ${data.price_aud.toString()}
            </Text>
          </HStack>
          {hasWorkPlacement ? (
            <>
              <Text fontSize="sm" lineHeight="5" color="fg.subtle">
                •
              </Text>
              <Text fontSize="sm" lineHeight="5" color="warning.deep" fontWeight="medium">
                <Trans>Requires work placement</Trans>
              </Text>
            </>
          ) : null}
        </HStack>
      </VStack>

      <Box w="full" h="full">
        <UnorderedList
          listStyleType="none"
          overflowY="auto"
          minHeight="100px"
          maxHeight="400px"
          p={0}
          m={0}
          w="full"
        >
          {data.description != null ? (
            <Text textStyle="sm" color="fg.muted" mb={4}>
              {data.description}
            </Text>
          ) : null}
          {schedulerData.length
            ? schedulerData.map((item, index: number) => (
                <ListItem key={item.id} w="full">
                  <VStack w="full" align="center" gap="0">
                    <CourseUnitOfCompetency unit={item} />
                    {index !== schedulerData.length - 1 && (
                      <Box bg="border.emphasis" w="2px" h="16px" />
                    )}
                  </VStack>
                </ListItem>
              ))
            : null}
        </UnorderedList>
      </Box>
    </VStack>
  );
}

function CourseUnitOfCompetency({ unit }: { unit: StudentScheduleItem }) {
  return (
    <Flex
      align="start"
      justify="center"
      direction="column"
      border="1px solid"
      borderColor="border.default"
      borderRadius="8px"
      padding="16px"
      bg="bg.subtle"
      w="full"
    >
      {unit?.['Unit Code'] ? (
        <Text color="fg.muted" textStyle="sm">
          {unit['Unit Code']}
        </Text>
      ) : null}
      <VStack align="start" justify="start" spacing="4px">
        <Text color="fg.emphasis" textStyle="md" fontWeight="semibold">
          {unit.Title}
        </Text>
      </VStack>
    </Flex>
  );
}
